// Small SVG line chart against elapsed time. The app has no charting library,
// so this draws just what the cook pages need: lines, shaded x-ranges (stall),
// low/high bands, horizontal and vertical reference lines, direct end labels
// and a crosshair tooltip.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

export const INK = {
  primary: '#0b0b0b',
  secondary: '#52514e',
  muted: '#898781',
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  surface: '#fcfcfb',
  shade: '#f0efec'
};

// Categorical slots in fixed order (validated: CVD-safe for adjacent pairs,
// and the first three for all pairs). Never cycle past eight.
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

const MARGIN = { top: 12, right: 104, bottom: 30, left: 56 };
const LABEL_GAP = 13;

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    background: INK.surface,
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    fontVariantNumeric: 'tabular-nums'
  },
  svg: { display: 'block', touchAction: 'none' },
  tooltip: {
    position: 'absolute',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid rgba(11,11,11,0.10)',
    borderRadius: 6,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    padding: '6px 8px',
    fontSize: 12,
    color: INK.secondary,
    whiteSpace: 'nowrap',
    zIndex: 2
  },
  tipRow: { display: 'flex', alignItems: 'center', gap: 6, lineHeight: '18px' },
  swatch: { width: 10, height: 2, borderRadius: 1, flex: 'none' },
  tipValue: { color: INK.primary, fontWeight: 600 },
  legend: { display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12, color: INK.secondary, padding: '4px 0 0 56px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6 }
}));

const hours = s => s / 3600;

// "Nice" tick step covering span in about `count` intervals.
function niceStep(span, count) {
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  return (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
}

function ticks(min, max, count) {
  const step = niceStep(max - min || 1, count);
  const out = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-9; v += step) out.push(v);
  return out;
}

// Index of the point with x nearest to `x` (points sorted by x).
function nearest(points, x) {
  let lo = 0;
  let hi = points.length - 1;
  if (hi < 0) return -1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid][0] < x) lo = mid + 1; else hi = mid;
  }
  if (lo > 0 && Math.abs(points[lo - 1][0] - x) < Math.abs(points[lo][0] - x)) lo--;
  return lo;
}

// Spread end labels so none sit closer than LABEL_GAP px apart.
function spreadLabels(labels, top, bottom) {
  const sorted = [...labels].sort((a, b) => a.y - b.y);
  for (let i = 1; i < sorted.length; i++) {
    sorted[i].y = Math.max(sorted[i].y, sorted[i - 1].y + LABEL_GAP);
  }
  const overflow = sorted.length ? sorted[sorted.length - 1].y - bottom : 0;
  if (overflow > 0) sorted.forEach(l => { l.y = Math.max(top, l.y - overflow); });
  return sorted;
}

function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/*
 * Props (all x values are seconds since cook start):
 *   xMax        right edge of the x axis
 *   series      [{ key, label, color, points: [[x, y]], strokeWidth, opacity,
 *                  endLabel, inTooltip, inLegend }]
 *   bands       [{ key, color, points: [[x, low, high]] }]    shaded low/high envelope
 *   xRanges     [{ key, from, to, label }]                    shaded vertical regions
 *   hLines      [{ key, y, label }]                           dashed reference lines
 *   vLines      [{ key, x, label }]                           event markers
 *   formatY     value -> axis/tooltip text
 *   tooltipExtra  x -> optional node appended to the tooltip
 */
const TimeChart = ({
  height = 320, xMax, series, bands = [], xRanges = [], hLines = [], vLines = [],
  formatY = v => String(v), yUnit = '', tooltipExtra, ariaLabel
}) => {
  const classes = useStyles();
  const rootRef = useRef(null);
  const width = useWidth(rootRef);
  const [hoverX, setHoverX] = useState(null);

  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotH = height - MARGIN.top - MARGIN.bottom;

  const [yMin, yMax] = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    const take = v => { if (v !== null && Number.isFinite(v)) { lo = Math.min(lo, v); hi = Math.max(hi, v); } };
    series.forEach(s => s.points.forEach(p => take(p[1])));
    bands.forEach(b => b.points.forEach(p => { take(p[1]); take(p[2]); }));
    hLines.forEach(l => take(l.y));
    if (!Number.isFinite(lo)) return [0, 1];
    const pad = (hi - lo) * 0.05 || 1;
    return [lo - pad, hi + pad];
  }, [series, bands, hLines]);

  const xMaxSafe = xMax > 0 ? xMax : 1;
  const sx = x => MARGIN.left + (x / xMaxSafe) * plotW;
  const sy = y => MARGIN.top + plotH - ((y - yMin) / (yMax - yMin)) * plotH;
  const path = pts => pts.filter(p => p[1] !== null)
    .map((p, i) => `${i ? 'L' : 'M'}${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`).join('');

  const xTicks = ticks(0, hours(xMaxSafe), Math.max(2, Math.floor(plotW / 90)));
  const yTicks = ticks(yMin, yMax, 5);

  // Series end labels and reference-line labels share the right gutter, so
  // they're spread together.
  const endLabels = spreadLabels([
    ...series.filter(s => s.endLabel && s.points.length)
      .map(s => ({ key: s.key, text: s.label, color: s.color, y: sy(s.points[s.points.length - 1][1]) })),
    ...hLines.map(l => ({ key: `h-${l.key}`, text: l.label, color: null, y: sy(l.y) }))
  ], MARGIN.top + 4, MARGIN.top + plotH);

  const onMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left - MARGIN.left) / plotW) * xMaxSafe;
    setHoverX(x < 0 || x > xMaxSafe ? null : x);
  };

  const tipSeries = hoverX === null ? [] : series.filter(s => s.inTooltip !== false).map(s => {
    const i = nearest(s.points, hoverX);
    return i === -1 ? null : { s, point: s.points[i] };
  }).filter(Boolean);
  const snapX = tipSeries.length ? tipSeries[0].point[0] : hoverX;
  const tipLeft = snapX !== null && sx(snapX) > width / 2;

  const legend = series.filter(s => s.inLegend !== false);

  if (!width) return <div ref={rootRef} className={classes.root} style={{ height }} />;

  return (
    <div ref={rootRef} className={classes.root}>
      <svg className={classes.svg} width={width} height={height} role="img" aria-label={ariaLabel}
        onPointerMove={onMove} onPointerLeave={() => setHoverX(null)}>
        {xRanges.map(r => (
          <g key={r.key}>
            <rect x={sx(r.from)} y={MARGIN.top} width={Math.max(1, sx(r.to) - sx(r.from))} height={plotH} fill={INK.shade} />
            {r.label && <text x={sx(r.from) + 4} y={MARGIN.top + 12} fontSize={11} fill={INK.muted}>{r.label}</text>}
          </g>
        ))}

        {yTicks.map(v => (
          <g key={`y${v}`}>
            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={sy(v)} y2={sy(v)} stroke={INK.grid} strokeWidth={1} />
            <text x={MARGIN.left - 6} y={sy(v)} dy="0.32em" textAnchor="end" fontSize={11} fill={INK.muted}>{formatY(v)}</text>
          </g>
        ))}
        <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={MARGIN.top + plotH} y2={MARGIN.top + plotH} stroke={INK.axis} />
        {xTicks.map(h => (
          <text key={`x${h}`} x={sx(h * 3600)} y={MARGIN.top + plotH + 18} textAnchor="middle" fontSize={11} fill={INK.muted}>{`${h}h`}</text>
        ))}
        {yUnit && <text x={MARGIN.left - 6} y={MARGIN.top - 2} textAnchor="end" fontSize={11} fill={INK.muted}>{yUnit}</text>}

        {bands.map(b => {
          const pts = b.points.filter(p => p[1] !== null && p[2] !== null);
          if (pts.length < 2) return null;
          const upper = pts.map(p => `${sx(p[0]).toFixed(1)},${sy(p[2]).toFixed(1)}`);
          const lower = pts.slice().reverse().map(p => `${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`);
          return <polygon key={b.key} points={[...upper, ...lower].join(' ')} fill={b.color} opacity={0.15} />;
        })}

        {hLines.map(l => (
          <g key={l.key}>
            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={sy(l.y)} y2={sy(l.y)} stroke={INK.secondary} strokeWidth={1} strokeDasharray="4 3" />
          </g>
        ))}

        {vLines.map(l => (
          <g key={l.key}>
            <line x1={sx(l.x)} x2={sx(l.x)} y1={MARGIN.top} y2={MARGIN.top + plotH} stroke={INK.secondary} strokeWidth={1} />
            <text x={sx(l.x) + 4} y={MARGIN.top + plotH - 6} fontSize={11} fill={INK.secondary}>{l.label}</text>
          </g>
        ))}

        {series.map(s => (
          <path key={s.key} d={path(s.points)} fill="none" stroke={s.color} strokeWidth={s.strokeWidth || 2}
            strokeOpacity={s.opacity === undefined ? 1 : s.opacity} strokeLinejoin="round" strokeLinecap="round" />
        ))}

        {endLabels.map(l => (
          <text key={`label-${l.key}`} x={MARGIN.left + plotW + 6} y={l.y} dy="0.32em" fontSize={11} fill={INK.secondary}>
            <tspan fill={l.color || INK.secondary}>{l.color ? '━ ' : '┅ '}</tspan>{l.text}
          </text>
        ))}

        {snapX !== null && (
          <g pointerEvents="none">
            <line x1={sx(snapX)} x2={sx(snapX)} y1={MARGIN.top} y2={MARGIN.top + plotH} stroke={INK.muted} strokeWidth={1} />
            {tipSeries.filter(t => t.point[1] !== null && t.s.endLabel).map(t => (
              <circle key={t.s.key} cx={sx(t.point[0])} cy={sy(t.point[1])} r={4} fill={t.s.color} stroke={INK.surface} strokeWidth={2} />
            ))}
          </g>
        )}
        <rect x={MARGIN.left} y={MARGIN.top} width={plotW} height={plotH} fill="transparent" />
      </svg>

      {snapX !== null && (
        <div className={classes.tooltip}
          style={{ top: MARGIN.top, ...(tipLeft ? { right: width - sx(snapX) + 10 } : { left: sx(snapX) + 10 }) }}>
          <div className={classes.tipRow} style={{ color: INK.primary }}>
            {`${Math.floor(snapX / 3600)}h ${String(Math.round((snapX % 3600) / 60)).padStart(2, '0')}m`}
          </div>
          {tipSeries.map(({ s, point }) => (
            <div key={s.key} className={classes.tipRow}>
              <span className={classes.swatch} style={{ background: s.color }} />
              <span className={classes.tipValue}>{point[1] === null ? '—' : formatY(point[1])}</span>
              <span>{s.label}</span>
            </div>
          ))}
          {tooltipExtra && tooltipExtra(snapX)}
        </div>
      )}

      {legend.length > 1 && (
        <div className={classes.legend}>
          {legend.map(s => (
            <span key={s.key} className={classes.legendItem}>
              <span className={classes.swatch} style={{ background: s.color, opacity: s.opacity === undefined ? 1 : Math.max(s.opacity, 0.5) }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeChart;
